import * as XLSX from "xlsx";

const PREFERRED_COLUMN_ORDER = [
  "uid",
  "name",
  "email",
  "phone",
  "role",
  "status",
  "createdAt",
  "updatedAt",
];

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value) && !(value instanceof Date);

const serializeFirestoreValue = (value: unknown): unknown => {
  if (value === null || value === undefined) return "";

  if (typeof value === "object" && value !== null && "toDate" in value && typeof (value as { toDate?: () => Date }).toDate === "function") {
    const date = (value as { toDate: () => Date }).toDate();
    return Number.isNaN(date.getTime()) ? "" : date.toISOString();
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? "" : value.toISOString();
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (isPlainObject(item) || Array.isArray(item)) {
          return JSON.stringify(item);
        }
        return serializeFirestoreValue(item);
      })
      .join("; ");
  }

  if (isPlainObject(value)) {
    return JSON.stringify(value);
  }

  return value;
};

export const flattenFirestoreRecord = (
  recordId: string,
  data: Record<string, unknown>,
  prefix = "",
): Record<string, string | number | boolean> => {
  const flat: Record<string, string | number | boolean> = {};

  const assign = (key: string, value: unknown) => {
    const serialized = serializeFirestoreValue(value);
    flat[key] = typeof serialized === "boolean" || typeof serialized === "number" ? serialized : String(serialized ?? "");
  };

  if (!prefix) {
    assign("uid", recordId);
  }

  Object.entries(data).forEach(([key, value]) => {
    const columnKey = prefix ? `${prefix}.${key}` : key;

    if (isPlainObject(value) && !("toDate" in value)) {
      Object.assign(flat, flattenFirestoreRecord(recordId, value as Record<string, unknown>, columnKey));
      return;
    }

    assign(columnKey, value);
  });

  return flat;
};

const sortColumns = (columns: string[]): string[] => {
  const preferred = PREFERRED_COLUMN_ORDER.filter((column) => columns.includes(column));
  const remaining = columns
    .filter((column) => !preferred.includes(column))
    .sort((a, b) => a.localeCompare(b, "pt-BR"));

  return [...preferred, ...remaining];
};

export const exportRecordsToExcel = (filename: string, rows: Record<string, string | number | boolean>[]) => {
  if (rows.length === 0) {
    throw new Error("Não há registros para exportar.");
  }

  const columnSet = new Set<string>();
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => columnSet.add(key));
  });

  const columns = sortColumns(Array.from(columnSet));
  const worksheetData = [
    columns,
    ...rows.map((row) => columns.map((column) => row[column] ?? "")),
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Dados");
  XLSX.writeFile(workbook, filename);
};
